import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { AlertTriangle, Bot, CheckCircle2, Loader2, ShieldAlert, UserRound, Video } from 'lucide-react';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import {
  preflightHandleChange, changeTikTokHandle, type HandleChangePreflight,
} from '@/lib/workerApi';
import { UserEmailHistory } from '@/components/UserEmailHistory';

/**
 * Change a creator's TikTok handle.
 *
 * Two refusals carry the weight here, and the dialog explains both rather than
 * just greying the button out:
 *
 *  - Another roster row already owns the handle. Writing it would re-point this
 *    profile onto that creator's roster row, and since everything is scoped by
 *    roster_creator_id, hand over their jobs, hires and message threads.
 *  - The creator is OAuth-linked. TikTok's open_id is authoritative and the
 *    daily refresh rewrites the handle from it, so the edit would not survive
 *    24 hours. Overridable, because ops occasionally needs the handle correct
 *    for a day, but never silently.
 */
export function ChangeTikTokHandleDialog({ user, open, onOpenChange }: {
  user: { id: string; tiktok_handle?: string | null; full_name?: string | null; email?: string | null } | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const qc = useQueryClient();
  const [handle, setHandle] = useState('');
  const [reason, setReason] = useState('');
  const [allowSwitch, setAllowSwitch] = useState(false);
  const [changeCoach, setChangeCoach] = useState(false);
  const [pre, setPre] = useState<HandleChangePreflight | null>(null);

  const reset = () => { setHandle(''); setReason(''); setAllowSwitch(false); setChangeCoach(false); setPre(null); };
  const close = (next: boolean) => { if (!next) reset(); onOpenChange(next); };

  // Strip what people paste: a leading @, a full profile URL, stray case.
  const clean = handle.trim().replace(/^https?:\/\/(www\.)?tiktok\.com\//i, '').replace(/^@+/, '').toLowerCase();
  const valid = /^[a-z0-9._]{2,24}$/.test(clean);

  const checkMutation = useMutation({
    mutationFn: () => preflightHandleChange(user!.id, clean),
    onSuccess: setPre,
    onError: (e: Error) => toast.error(e.message),
  });

  const applyMutation = useMutation({
    mutationFn: () => changeTikTokHandle({
      userId: user!.id, newHandle: clean, reason: reason.trim(),
      allowAccountSwitch: allowSwitch, changeCoach,
    }),
    onSuccess: (res) => {
      toast.success(`Username changed to @${res.new_handle}`);
      qc.invalidateQueries({ queryKey: ['users'] });
      qc.invalidateQueries({ queryKey: ['creator-users'] });
      qc.invalidateQueries({ queryKey: ['user-email-history', user!.id, 'handle'] });
      close(false);
    },
    onError: (e: Error) => toast.error(e.message),
  });

  // The resolved account is not the stored one: a switch to a different TikTok
  // account rather than a rename of the same one.
  const isSwitch = pre?.account_switch === true;
  const work = pre?.active_work;
  const hasLiveWork =
    !!work && (work.live_hires > 0 || work.unpaid_completed > 0 ||
               work.pending_applications > 0 || work.videos_in_review > 0);
  // A switch is allowed — creators do run several accounts — but never
  // implicitly, so it gates the button until it is acknowledged.
  const canApply =
    valid && reason.trim().length >= 10 && !applyMutation.isPending &&
    pre?.ok === true && (!isSwitch || allowSwitch);

  if (!user) return null;

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Change TikTok username</DialogTitle>
          <DialogDescription>
            {user.full_name || user.email || 'This creator'} is currently{' '}
            <strong className="text-foreground">@{user.tiktok_handle ?? '—'}</strong>. This updates
            their profile, roster row and coach seat together, and resets their featured videos.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="rounded-md border bg-muted/20 p-2.5">
            <p className="mb-1.5 text-xs font-medium text-muted-foreground">Previous username changes</p>
            <UserEmailHistory userId={user.id} kind="handle" compact />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="new-handle">New username</Label>
            <Input
              id="new-handle" autoComplete="off" placeholder="newusername"
              value={handle}
              onChange={(e) => { setHandle(e.target.value); setPre(null); }}
            />
            {handle.trim() && (
              <p className="text-xs text-muted-foreground">
                Will be saved as <span className="font-mono">@{clean || '—'}</span>
                {!valid && <span className="text-destructive"> — 2-24 characters, letters/numbers/dots/underscores only</span>}
              </p>
            )}
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="handle-reason">Reason <span className="text-destructive">*</span></Label>
            <Textarea
              id="handle-reason" rows={2} value={reason}
              placeholder="e.g. Typo at signup — creator asked in ticket #456"
              onChange={(e) => setReason(e.target.value)}
            />
          </div>

          {pre?.ok && (
            <div className="rounded-md border border-green-200 bg-green-50 p-3 text-sm">
              <div className="flex items-center gap-2 font-medium text-green-800">
                <CheckCircle2 className="h-4 w-4" /> No conflicts — safe to apply
              </div>
              <ul className="mt-2 space-y-0.5 text-xs text-green-900">
                <li>Updates their profile and roster row{pre.coach_seat && changeCoach && ', and rebuilds their coach'}</li>
                <li className="flex items-start gap-1">
                  <Video className="mt-0.5 h-3 w-3 shrink-0" />
                  Clears {pre.will_reset?.showcase_videos ?? 0} featured video(s); they re-sync from the
                  new username the next time the creator opens the app
                </li>
              </ul>
            </div>
          )}

          {pre && !pre.ok && (
            <div className="rounded-md border border-destructive/30 bg-destructive/5 p-3 text-sm">
              <div className="flex items-center gap-2 font-medium text-destructive">
                <AlertTriangle className="h-4 w-4" />
                {pre.error === 'unchanged' ? 'Already their username'
                  : pre.error === 'invalid_handle' ? 'Not a valid TikTok username'
                  : pre.error === 'user_not_found' ? 'Creator not found'
                  : `${pre.blocking_count} issue(s)`}
              </div>
              {pre.message && <p className="mt-1 text-xs text-muted-foreground">{pre.message}</p>}
              <ul className="mt-2 space-y-1.5 text-xs">
                {(pre.conflicts ?? []).filter(c => c.blocking).map((c, i) => (
                  <li key={i}>
                    <Badge variant="outline" className="mr-1 font-mono text-[10px]">{c.table}</Badge>
                    <span className="text-muted-foreground">
                      {c.message ?? 'already uses this username'}
                      {c.other_hires != null && c.other_hires > 0 && ` (${c.other_hires} hires on that row)`}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* The coach seat. A separate identity with its own handle and its
              own self-service change flow, so the admin is asked rather than
              it being moved silently. */}
          {pre?.coach_seat && (
            <div className="rounded-md border bg-muted/20 p-3 text-sm">
              <div className="flex items-center gap-2 font-medium">
                <Bot className="h-4 w-4" /> This creator has a BMC Coach seat
              </div>
              <p className="mt-1 text-xs text-muted-foreground">
                Coach currently runs on{' '}
                <span className="font-mono font-medium text-foreground">
                  @{pre.coach_seat.tiktok_username ?? '—'}
                </span>
                {pre.coach_seat.differs_from_app
                  ? ' — a different account from their app profile. That is allowed, and usually means it should be left alone.'
                  : ' — the same account as their app profile.'}
              </p>

              {pre.coach_seat.is_building ? (
                <p className="mt-2 text-xs font-medium text-amber-700">
                  Their coach is being built right now, so it can&apos;t be changed until that finishes.
                </p>
              ) : (
                <>
                  <label className="mt-2 flex items-start gap-2 text-xs font-medium">
                    <Checkbox className="mt-0.5" checked={changeCoach}
                              onCheckedChange={(v) => setChangeCoach(v === true)} />
                    <span>Change the coach username too, and rebuild it</span>
                  </label>
                  {changeCoach && (
                    <p className="mt-1.5 border-l-2 border-amber-400 pl-2 text-xs text-muted-foreground">
                      Same as the creator doing it themselves: their coach is retrained on the new
                      account&apos;s videos. The old account&apos;s training data is discarded, it takes
                      about 15 minutes, and the coach is unavailable until it finishes. Their existing
                      referral links keep working.
                    </p>
                  )}
                </>
              )}
            </div>
          )}

          {/* Who this handle actually belongs to on TikTok Shop. Shown before
              anything is committed: the handle alone cannot distinguish a
              rename from a different person's account. */}
          {pre?.lookup?.found && (
            <div className="rounded-md border bg-muted/20 p-3 text-sm">
              <div className="flex items-center gap-2">
                {pre.lookup.avatar_url
                  ? <img src={pre.lookup.avatar_url} alt="" className="h-8 w-8 rounded-full object-cover" />
                  : <UserRound className="h-8 w-8 rounded-full bg-muted p-1.5 text-muted-foreground" />}
                <div className="min-w-0">
                  <p className="font-medium">@{pre.lookup.username}</p>
                  <p className="truncate text-xs text-muted-foreground">
                    {pre.lookup.nickname ?? 'Verified on TikTok Shop'}
                    {pre.lookup.follower_count != null && ` · ${pre.lookup.follower_count.toLocaleString()} followers`}
                  </p>
                </div>
              </div>
              {!isSwitch && pre.ok && (
                <p className="mt-2 text-xs text-green-700">
                  Same TikTok account as now — this is a rename, and it will stick.
                </p>
              )}
            </div>
          )}

          {/* A genuinely different account. Allowed, but the brands who hired
              this creator hired the OLD handle, so they are named here. */}
          {isSwitch && (
            <div className="rounded-md border border-amber-300 bg-amber-50 p-3 text-sm">
              <div className="flex items-center gap-2 font-medium text-amber-900">
                <ShieldAlert className="h-4 w-4" /> This is a different TikTok account
              </div>
              <p className="mt-1 text-xs text-amber-900">
                @{clean} is not a rename of @{pre?.target?.current_handle ?? '—'} — it is a separate
                account. Their GMV, followers and videos will be replaced with this
                account&apos;s numbers.
              </p>

              {hasLiveWork && (
                <div className="mt-2 rounded border border-amber-400 bg-amber-100/60 p-2 text-xs text-amber-900">
                  <p className="font-semibold">This creator has work in progress.</p>
                  <ul className="mt-1 list-inside list-disc">
                    {work!.live_hires > 0 && <li>{work!.live_hires} live hire(s)</li>}
                    {work!.unpaid_completed > 0 && <li>{work!.unpaid_completed} completed but unpaid</li>}
                    {work!.pending_applications > 0 && <li>{work!.pending_applications} pending application(s)</li>}
                    {work!.videos_in_review > 0 && <li>{work!.videos_in_review} video(s) awaiting review</li>}
                  </ul>
                  {work!.brands.length > 0 && (
                    <p className="mt-1.5">
                      {work!.brands.join(', ')} hired <strong>@{pre?.target?.current_handle}</strong> and
                      will see <strong>@{clean}</strong> delivering. Let them know, or they will think
                      the wrong creator took the job.
                    </p>
                  )}
                </div>
              )}

              <label className="mt-2 flex items-center gap-2 text-xs font-medium text-amber-900">
                <Checkbox checked={allowSwitch} onCheckedChange={(v) => setAllowSwitch(v === true)} />
                I know this is a different account — switch it
              </label>
            </div>
          )}
        </div>

        <DialogFooter className="gap-2 sm:gap-2">
          <Button variant="ghost" onClick={() => close(false)}>Cancel</Button>
          <Button variant="outline" onClick={() => checkMutation.mutate()} disabled={!valid || checkMutation.isPending}>
            {checkMutation.isPending && <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />}
            Check for conflicts
          </Button>
          <Button variant="destructive" onClick={() => applyMutation.mutate()} disabled={!canApply}
                  title={pre ? undefined : 'Run the conflict check first'}>
            {applyMutation.isPending && <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />}
            Change username
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
